import type { MessageFeedback } from "~/components/assistant/types";

// ============================================================================
// Constants
// ============================================================================

// Tools to ignore in the UI
export const TOOL_IGNORE_LIST = ["catalog_api", "update_long_term_memory"];

// ============================================================================
// Event Types
// ============================================================================

/**
 * Base interface for all stream events
 */
export interface BaseStreamEvent {
  kind: string;
  trace_id?: string;
  timestamp?: string;
}

/**
 * Tool call/return event from the stream
 */
export interface ToolCallEvent extends BaseStreamEvent {
  kind: "tool_call" | "tool_return";
  tool_call_id?: string;
  tool?: string;
  tool_name?: string;
  args?: unknown;
  content?: unknown;
}

/**
 * Model chat event containing content, figures, dataframes, values, or filters
 */
export interface ModelChatEvent extends BaseStreamEvent {
  kind: "model_chat";
  message_id?: string;
  content?: string;
  figures?: unknown[];
  dataframes?: unknown[];
  values?: unknown[];
  text_files?: unknown[];
  blob_files?: unknown[];
  filters?: unknown;
  message_feedback?: MessageFeedback | null;
}

/**
 * User chat event
 */
export interface UserChatEvent extends BaseStreamEvent {
  kind: "user_chat";
  id?: string;
  content?: string;
}

/**
 * Error event (can have kind="error" or just error property)
 */
export interface ErrorEvent {
  kind?: "error" | string;
  trace_id?: string;
  timestamp?: string;
  error?: string;
  message?: string;
}

/**
 * Warning event
 */
export interface WarningEvent extends BaseStreamEvent {
  kind: "warning";
  message?: string;
}

/**
 * Cancelled event
 */
export interface CancelledEvent extends BaseStreamEvent {
  kind: "cancelled";
}

/**
 * Message persisted event
 */
export interface MessagePersistedEvent extends BaseStreamEvent {
  kind: "message_persisted";
  client_message_id?: string;
  message_id?: string;
}

/**
 * Conversation name event
 */
export interface ConversationNameEvent extends BaseStreamEvent {
  kind: "conversation_name";
  conversation_id?: string;
  display_name?: string;
}

/**
 * Model thinking event containing reasoning/thinking content
 */
export interface ModelThinkingEvent extends BaseStreamEvent {
  kind: "model_thinking";
  content?: string;
}

/**
 * Heartbeat event (ignored)
 */
export interface HeartbeatEvent extends BaseStreamEvent {
  kind: "heartbeat";
}

/**
 * Union type for all possible stream events
 */
export type StreamEvent =
  | ToolCallEvent
  | ModelChatEvent
  | UserChatEvent
  | ErrorEvent
  | WarningEvent
  | CancelledEvent
  | MessagePersistedEvent
  | ConversationNameEvent
  | ModelThinkingEvent
  | HeartbeatEvent;
